import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, timestamp, numeric, integer, date, pgEnum } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { contacts, accounts, trustBeneficiaries } from "./schema-crm.js";

// ============================================================================
// ADDRESS MANAGEMENT TABLES (3 tables)
// Centralized address storage with API validation support
// Foundation for future security tables (collateral, property valuations)
// ============================================================================

// 10. Address Type Enum
export const addressTypeEnum = pgEnum("address_type", [
  "residential",                    // Home/personal address
  "postal",                         // Mailing address (PO Box, etc.)
  "registered_office",              // Company registered office (ASIC requirement)
  "principal_place_of_business",    // Where business is conducted
  "property",                       // Real estate/property address (for collateral)
  "security"                        // Security/collateral address
]);

// 11. Addresses Table (Centralized address storage)
export const addresses = pgTable("crm_x_addresses", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Address components (Australian format)
  unitNumber: varchar("unit_number", { length: 20 }),           // Unit/Suite/Level number
  streetNumber: varchar("street_number", { length: 20 }),       // Street number
  streetName: varchar("street_name", { length: 255 }),          // Street name
  streetType: varchar("street_type", { length: 50 }),           // Road, Street, Avenue, etc.
  addressLine2: varchar("address_line_2", { length: 255 }),     // Additional info (Building name, etc.)
  suburb: varchar("suburb", { length: 100 }).notNull(),         // Suburb/City/Town
  state: varchar("state", { length: 50 }).notNull(),            // State/Territory (NSW, VIC, QLD, etc.)
  postcode: varchar("postcode", { length: 10 }).notNull(),      // Postal code
  country: varchar("country", { length: 100 }).default("Australia").notNull(),

  // Formatted address (full address string from API or constructed)
  formattedAddress: text("formatted_address"),

  // Geocoding data (for mapping and location services)
  latitude: numeric("latitude", { precision: 10, scale: 7 }),   // -90 to 90
  longitude: numeric("longitude", { precision: 11, scale: 7 }), // -180 to 180

  // API validation data
  placeId: varchar("place_id", { length: 255 }),                // Google Place ID or similar
  isVerified: integer("is_verified").default(0).notNull(),      // 1 = verified by API, 0 = manual entry
  verifiedAt: timestamp("verified_at"),

  // Metadata
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 12. Contact Addresses Junction Table (Many-to-many: contacts to addresses)
export const contactAddresses = pgTable("crm_x_contact_addresses", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }).notNull(),
  addressId: uuid("address_id").references(() => addresses.id, { onDelete: "cascade" }).notNull(),
  addressType: addressTypeEnum("address_type").default("residential").notNull(),
  isPrimary: integer("is_primary").default(0).notNull(),        // 1 = primary address of this type
  effectiveFrom: date("effective_from"),                         // When address became active
  effectiveTo: date("effective_to"),                             // When address was no longer active
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 13. Account Addresses Junction Table (Many-to-many: accounts to addresses)
export const accountAddresses = pgTable("crm_x_account_addresses", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "cascade" }).notNull(),
  addressId: uuid("address_id").references(() => addresses.id, { onDelete: "cascade" }).notNull(),
  addressType: addressTypeEnum("address_type").default("principal_place_of_business").notNull(),
  isPrimary: integer("is_primary").default(0).notNull(),        // 1 = primary address of this type
  effectiveFrom: date("effective_from"),
  effectiveTo: date("effective_to"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 14. Account Beneficial Owners (ISSUE-222: Restored for TypeScript compatibility)
export const uboSourceEnum = pgEnum("ubo_source", ["manual", "pdf_extraction", "asic_lookup", "trust_beneficiary", "client_declaration"]);

export const accountBeneficialOwners = pgTable("crm_x_account_beneficial_owners", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "cascade" }).notNull(),
  contactId: uuid("contact_id").references(() => contacts.id).notNull(),
  ownershipPercentage: numeric("ownership_percentage", { precision: 5, scale: 2 }),
  role: varchar("role", { length: 50 }),
  source: uboSourceEnum("source").default("manual").notNull(),
  sourceDocumentId: uuid("source_document_id"),
  extractionConfidence: numeric("extraction_confidence", { precision: 3, scale: 2 }),
  sourceTrustBeneficiaryId: uuid("source_trust_beneficiary_id").references(() => trustBeneficiaries.id),
  verifiedBy: uuid("verified_by"),
  verifiedAt: timestamp("verified_at"),
  isVerified: integer("is_verified").default(0),
  isActive: integer("is_active").default(1),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// ============================================================================
// ADDRESS MANAGEMENT ZOD SCHEMAS
// ============================================================================

// Address Management Schemas
export const insertAddressSchema = createInsertSchema(addresses).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  verifiedAt: true
}).extend({
  latitude: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
  longitude: z.string().nullable().optional().transform(val => val && val.trim() ? val : null),
});
export const updateAddressSchema = insertAddressSchema.partial();
export const selectAddressSchema = createSelectSchema(addresses);

export const insertContactAddressSchema = createInsertSchema(contactAddresses).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateContactAddressSchema = insertContactAddressSchema.partial();
export const selectContactAddressSchema = createSelectSchema(contactAddresses);

export const insertAccountAddressSchema = createInsertSchema(accountAddresses).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const updateAccountAddressSchema = insertAccountAddressSchema.partial();
export const selectAccountAddressSchema = createSelectSchema(accountAddresses);

// Account Beneficial Owners Schemas (ISSUE-222: Restored for TypeScript compatibility)
export const insertAccountBeneficialOwnerSchema = createInsertSchema(accountBeneficialOwners).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  verifiedAt: true
});
export const updateAccountBeneficialOwnerSchema = insertAccountBeneficialOwnerSchema.partial();
export const selectAccountBeneficialOwnerSchema = createSelectSchema(accountBeneficialOwners);

// ============================================================================
// ADDRESS MANAGEMENT TYPES
// ============================================================================

// Address Management Types
export type InsertAddress = z.infer<typeof insertAddressSchema>;
export type UpdateAddress = z.infer<typeof updateAddressSchema>;
export type Address = typeof addresses.$inferSelect;

export type InsertContactAddress = z.infer<typeof insertContactAddressSchema>;
export type UpdateContactAddress = z.infer<typeof updateContactAddressSchema>;
export type ContactAddress = typeof contactAddresses.$inferSelect;

export type InsertAccountAddress = z.infer<typeof insertAccountAddressSchema>;
export type UpdateAccountAddress = z.infer<typeof updateAccountAddressSchema>;
export type AccountAddress = typeof accountAddresses.$inferSelect;

// Account Beneficial Owner Types (ISSUE-222: Restored for TypeScript compatibility)
export type InsertAccountBeneficialOwner = z.infer<typeof insertAccountBeneficialOwnerSchema>;
export type UpdateAccountBeneficialOwner = z.infer<typeof updateAccountBeneficialOwnerSchema>;
export type AccountBeneficialOwner = typeof accountBeneficialOwners.$inferSelect;
